import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useNavigate } from 'react-router-dom';
import { CardList } from 'components/molecules/CardList';
import { EButtonVariants } from 'constants/Buttons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EIconTypes } from 'constants/Icons';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EPerPages, ISortOptions } from 'models/table';
import { Pagination } from 'components/molecules/Pagination';
import { useLang } from 'models/langContext';
import {
  ComponentsAttachmentsTableColumns,
  mapComponentsAttachmentsToDataSource,
} from '../helpers';
import { ComponentsAttachmentsCard } from '../ComponentsAttachmentsCard';

export enum EComponentAttachmentsLabel {
  dateAndTime = 'componentDetails.attachments.DateAndTime.column',
  attachmentName = 'componentDetails.attachments.attachmentName.column',
  attachmentSize = 'componentDetails.attachments.attachmentSize.column',
  transactionType = 'componentDetails.attachments.transactionType.column',
  addAttachment = 'componentDetails.attachments.addAttachment.button',
}

interface IComponentsAttachments {
  idAncillary: string | undefined;
  hideManagement: boolean;
}
const ComponentsAttachments = ({ idAncillary, hideManagement }: IComponentsAttachments) => {
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslations();
  const {
    getComponentsAttachmentsData,
    componentsAttachments,
    paginatorComponentsAttachments,
    transactionType,
    getTransactionType,
  } = useAncillaries();
  const { longDateFormat, midDateFormat } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const { selectedLanguage } = useLang();

  useEffect(() => {
    getTransactionType();
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getDataInit() {
      if (!componentsAttachments.length) {
        setIsLoading(true);
      }
      setIsPaginationLoading(true);
      if (idAncillary) {
        await getComponentsAttachmentsData(idAncillary, { currentPage, perPage, sort });
      }
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [idAncillary, currentPage, perPage, sort]);

  return (
    <div>
      {!hideManagement && (
        <Grid
          size={{ xs: 12 }}
          container
          sx={{ display: 'flex', justifyContent: 'flex-end', margin: '24px 0' }}
        >
          <Button
            variant={EButtonVariants.contained}
            icon={EIconTypes.plus}
            label={t(EComponentAttachmentsLabel.addAttachment)}
            onClick={() =>
              idAncillary
                ? navigate(ERouteLinks.ComponentsAddAttachment.replace(':id', idAncillary))
                : {}
            }
          />
        </Grid>
      )}
      {isDesktop ? (
        <Table
          columns={ComponentsAttachmentsTableColumns({
            ancillaryId: idAncillary ?? '',
            handleView: (id: string) =>
              window.open(
                ERouteLinks.ComponentsViewAttachment.replace(':id', id).replace(
                  ':ancillaryId',
                  idAncillary ?? '',
                ),
                '_blank',
              ),
            handleDelete: (id: string) =>
              navigate(
                ERouteLinks.ComponentsDeleteAttachment.replace(':id', id).replace(
                  ':ancillaryId',
                  idAncillary ?? '',
                ),
                { state: { componentsAttachments } },
              ),
            dateFormat: longDateFormat,
            hideManagement,
          })}
          dataSource={mapComponentsAttachmentsToDataSource(
            componentsAttachments,
            transactionType,
            midDateFormat,
          )}
          onSort={setSort}
          loading={isLoading === true}
        />
      ) : (
        <CardList
          items={mapComponentsAttachmentsToDataSource(
            componentsAttachments,
            transactionType,
            midDateFormat,
          )}
          render={(data, index) => (
            <ComponentsAttachmentsCard
              data={data}
              key={index}
              idAncillary={idAncillary}
              handleView={(id: string) =>
                window.open(
                  ERouteLinks.ComponentsViewAttachment.replace(':id', id).replace(
                    ':ancillaryId',
                    idAncillary ?? '',
                  ),
                  '_blank',
                )
              }
              handleDelete={(id: string) =>
                navigate(
                  ERouteLinks.ComponentsDeleteAttachment.replace(':id', id).replace(
                    ':ancillaryId',
                    idAncillary ?? '',
                  ),
                  { state: { componentsAttachments } },
                )
              }
            />
          )}
        />
      )}
      {paginatorComponentsAttachments && (
        <Pagination
          count={paginatorComponentsAttachments?.totalElements}
          perPage={perPage}
          onChangePage={(page) => {
            setCurrentPage(page);
          }}
          onChangePerPage={(value) => {
            setPerPage(value);
          }}
          current={currentPage}
          isLoading={isPaginationLoading}
        />
      )}
    </div>
  );
};

export { ComponentsAttachments };
